import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EsraPipingAPIClient, FileResponse, SearchRequest } from 'src/app/shared/models/autogenerated-piping';

/**
 * Handles all Excel export calls for the Piping Dictionaries admin tabs.
 *
 * Each method delegates to the corresponding autogenerated `EsraPipingAPIClient`
 * method, passing `undefined` for all optional filter parameters so the BE
 * always exports the full dataset.
 *
 * All endpoints return an Excel blob with Content-Type:
 *   application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
 */
@Injectable({ providedIn: 'root' })
export class DictionaryExportService {
  private readonly esraApiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);

  /** Exports the Common Notes dictionary to Excel. */
  exportCommonNotes(): Observable<FileResponse> {
    return this.esraApiClient.exportCommonNotes(undefined, undefined);
  }

  /** Exports the Services dictionary to Excel. */
  exportServices(): Observable<FileResponse> {
    return this.esraApiClient.exportServices(undefined, undefined);
  }

  /** Exports the Service Details dictionary to Excel. */
  exportServiceDetails(): Observable<FileResponse> {
    return this.esraApiClient.exportServiceDetails(undefined, undefined);
  }

  /** Exports the Pressure & Temperature Tables dictionary to Excel. */
  exportPressureTemperature(): Observable<FileResponse> {
    // All four filter params (searchText, showAll, sortColumn, sortDirection) are omitted
    // so the BE exports the complete unfiltered dataset.
    return this.esraApiClient.exportPressureTemperature(undefined, undefined, undefined, undefined);
  }

  /** Exports the Branch Tables dictionary to Excel. */
  exportBranchTables(): Observable<FileResponse> {
    return this.esraApiClient.exportBranchTables(undefined, undefined);
  }

  /** Exports the Branch Table Codes dictionary to Excel. */
  exportBranchTableCodes(): Observable<FileResponse> {
    return this.esraApiClient.exportBranchTableCodes(undefined);
  }

  /**
   * Exports the Component Catalog to Excel.
   * An empty `SearchRequest` is sent so the BE exports all records regardless of filters.
   */
  exportComponentCatalog(): Observable<FileResponse> {
    const emptyRequest: SearchRequest = new SearchRequest({ perPage: 0, page: 0, mainSearch: '', filters: [], orderBy: '' });
    return this.esraApiClient.exportComponentCatalogByType(emptyRequest);
  }

  /** Exports the Component Properties dictionary to Excel. */
  exportComponentProperties(): Observable<FileResponse> {
    // group, type, and searchText are all omitted so the BE exports the full dataset.
    return this.esraApiClient.exportComponentProperties(undefined, undefined, undefined);
  }

  /** Exports the Valve Properties dictionary to Excel. */
  exportValveProperties(): Observable<FileResponse> {
    // group, type, and searchText are all omitted so the BE exports the full dataset.
    return this.esraApiClient.exportValveProperties(undefined, undefined, undefined);
  }

  /** Exports the Applications dictionary to Excel. */
  exportApplications(): Observable<FileResponse> {
    return this.esraApiClient.exportApplications(undefined, undefined);
  }

  /** Exports the Business Units dictionary to Excel. */
  exportBusinessUnits(): Observable<FileResponse> {
    return this.esraApiClient.exportBusinessUnits(undefined, undefined);
  }

  /** Exports the Specifications dictionary to Excel. */
  exportSpecifications(): Observable<FileResponse> {
    return this.esraApiClient.exportSpecifications(undefined, undefined);
  }

  /**
   * Exports the Pipe Class Summary Properties dictionary to Excel.
   * @param propertyName Optional filter to export only one of the 7 property fields.
   */
  exportPipeClassSummaryProperties(propertyName?: string): Observable<FileResponse> {
    return this.esraApiClient.exportPipeClassSummaryProperties(propertyName, undefined, undefined);
  }
}
